import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Brain, Plus, Loader2, X, Search, Merge, PlusCircle, XCircle, Zap, History, ArrowLeftRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/common/Badge';
import { cn } from '@/utils/cn';
import { skillsService } from '@/services/skills.service';
import type { Skill, SkillVersion, SkillExtractionJob, SkillExtractionCandidate } from '@/types';

export const SkillsPage: React.FC = () => {
  const { id: skillIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | 'common' | 'topic'>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');

  // Detail panel
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [versions, setVersions] = useState<SkillVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [settingVersion, setSettingVersion] = useState(false);
  const [compareVersions, setCompareVersions] = useState<[SkillVersion | null, SkillVersion | null]>([null, null]);
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', description: '', skill_type: 'topic' as 'common' | 'topic',
    rules: [''], few_shot_examples: [{ customer: '', agent: '' }],
  });
  const [isCreating, setIsCreating] = useState(false);

  // Extraction results modal
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [extractionJob, setExtractionJob] = useState<SkillExtractionJob | null>(null);
  const [candidates, setCandidates] = useState<SkillExtractionCandidate[]>([]);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [processingCandidateId, setProcessingCandidateId] = useState<string | null>(null);
  const [mergeTargetSkillId, setMergeTargetSkillId] = useState<string>('');
  const [showMergeSelect, setShowMergeSelect] = useState<string | null>(null);

  useEffect(() => {
    loadSkills();
  }, []);

  // When skills list loads and URL has an ID, select that skill
  useEffect(() => {
    if (skillIdFromUrl && skills.length > 0 && !selectedSkill) {
      const skill = skills.find((s) => s.id === skillIdFromUrl);
      if (skill) handleSelectSkill(skill);
    }
  }, [skillIdFromUrl, skills]);

  const loadSkills = async () => {
    setLoading(true);
    try {
      const data = await skillsService.getSkills();
      setSkills(data);
    } catch {
      toast.error('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSkill = async (skill: Skill) => {
    setSelectedSkill(skill);
    navigate('/skills/' + skill.id);
    setVersionsLoading(true);
    setCompareVersions([null, null]);
    try {
      const v = await skillsService.getVersions(skill.id);
      setVersions(v);
    } catch {
      toast.error('Failed to load versions');
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleSetVersion = async (version: number) => {
    if (!selectedSkill) return;
    setSettingVersion(true);
    try {
      await skillsService.setVersion(selectedSkill.id, version);
      toast.success(`Version ${version} activated`);
      await loadSkills();
      // Refresh detail
      const updated = await skillsService.getSkill(selectedSkill.id);
      setSelectedSkill(updated);
      const v = await skillsService.getVersions(selectedSkill.id);
      setVersions(v);
    } catch {
      toast.error('Failed to set version');
    } finally {
      setSettingVersion(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim()) { toast.error('Name is required'); return; }
    setIsCreating(true);
    try {
      await skillsService.createSkill({
        name: createForm.name.trim(),
        description: createForm.description.trim() || undefined,
        skill_type: createForm.skill_type,
        rules: createForm.rules.filter(r => r.trim()),
        few_shot_examples: createForm.few_shot_examples.filter(ex => ex.customer.trim() && ex.agent.trim()),
      });
      toast.success('Skill created');
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', skill_type: 'topic', rules: [''], few_shot_examples: [{ customer: '', agent: '' }] });
      loadSkills();
    } catch {
      toast.error('Failed to create skill');
    } finally {
      setIsCreating(false);
    }
  };

  const openExtractionResults = async (jobId: string) => {
    setShowExtractionModal(true);
    setExtractionLoading(true);
    try {
      const result = await skillsService.getExtractionJob(jobId);
      setExtractionJob(result.job);
      setCandidates(result.candidates);
    } catch {
      toast.error('Failed to load extraction results');
    } finally {
      setExtractionLoading(false);
    }
  };

  // Check URL params for extraction job ID on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jobId = params.get('extraction_job');
    if (jobId) openExtractionResults(jobId);
  }, []);

  const handleMergeCandidate = async (candidateId: string, targetSkillId: string) => {
    setProcessingCandidateId(candidateId);
    try {
      await skillsService.mergeCandidate(candidateId, targetSkillId);
      toast.success('Candidate merged into skill');
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: 'merged' as const } : c));
      setShowMergeSelect(null);
      loadSkills();
    } catch {
      toast.error('Failed to merge candidate');
    } finally {
      setProcessingCandidateId(null);
    }
  };

  const handleCreateFromCandidate = async (candidateId: string) => {
    setProcessingCandidateId(candidateId);
    try {
      await skillsService.createFromCandidate(candidateId);
      toast.success('Skill created from candidate');
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: 'created' as const } : c));
      loadSkills();
    } catch {
      toast.error('Failed to create skill');
    } finally {
      setProcessingCandidateId(null);
    }
  };

  const handleDismissCandidate = async (candidateId: string) => {
    setProcessingCandidateId(candidateId);
    try {
      await skillsService.dismissCandidate(candidateId);
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: 'dismissed' as const } : c));
    } catch {
      toast.error('Failed to dismiss');
    } finally {
      setProcessingCandidateId(null);
    }
  };

  // Filtered skills
  const filteredSkills = skills.filter(s => {
    if (searchQuery && !s.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (typeFilter && s.skill_type !== typeFilter) return false;
    if (statusFilter && s.status !== statusFilter) return false;
    return true;
  });

  const getCandidateStatusBadge = (status: string): 'warning' | 'info' | 'success' | 'default' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'merged': return 'info';
      case 'created': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const parseRulesArray = (rules: string[] | string): string[] => {
    if (Array.isArray(rules)) return rules;
    try { return JSON.parse(rules as string); } catch { return []; }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Skills</h1>
          <p className="text-gray-500 mt-1">Manage behavioral skills extracted from training sessions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Skill
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search skills..."
            className="w-full pl-9 pr-4 py-2 border-2 border-[#1a1a2e] rounded-xl text-sm focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | 'common' | 'topic')}
          className="px-3 py-2 border-2 border-[#1a1a2e] rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
        >
          <option value="">All Types</option>
          <option value="common">Common</option>
          <option value="topic">Topic</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | 'active' | 'inactive')}
          className="px-3 py-2 border-2 border-[#1a1a2e] rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Content */}
      <div className="flex gap-6">
        {/* Skills Table */}
        <div className={cn('flex-1 min-w-0', selectedSkill ? 'max-w-[60%]' : '')}>
          {loading ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
              <Brain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No skills found</h3>
              <p className="text-gray-500 text-sm">Create a skill or extract from training sessions.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#1a1a2e] bg-gray-50">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ver</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Rules</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Examples</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Agents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredSkills.map((skill) => (
                    <tr
                      key={skill.id}
                      onClick={() => handleSelectSkill(skill)}
                      className={cn(
                        'hover:bg-gray-50 transition-colors cursor-pointer',
                        selectedSkill?.id === skill.id ? 'bg-violet-50' : ''
                      )}
                    >
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{skill.name}</p>
                        {skill.description && (
                          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{skill.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={skill.skill_type === 'common' ? 'info' : 'default'}>{skill.skill_type}</Badge>
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={skill.status === 'active' ? 'success' : 'default'}>{skill.status}</Badge>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">v{skill.version}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{skill.rules?.length || 0}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{skill.few_shot_examples?.length || 0}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{skill.agent_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedSkill && (
          <div className="w-[40%] bg-white rounded-xl border-2 border-[#1a1a2e] p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{selectedSkill.name}</h3>
              <button onClick={() => { setSelectedSkill(null); navigate('/skills'); }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Badge variant={selectedSkill.skill_type === 'common' ? 'info' : 'default'}>{selectedSkill.skill_type}</Badge>
              <Badge variant={selectedSkill.status === 'active' ? 'success' : 'default'}>{selectedSkill.status}</Badge>
              <span className="text-sm text-gray-500">v{selectedSkill.version}</span>
            </div>

            {selectedSkill.description && (
              <p className="text-sm text-gray-600 mb-4">{selectedSkill.description}</p>
            )}

            {/* Rules */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Rules ({selectedSkill.rules?.length || 0})</h4>
              <div className="space-y-1">
                {(selectedSkill.rules || []).map((rule, i) => (
                  <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2">- {rule}</p>
                ))}
              </div>
            </div>

            {/* Examples */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Examples ({selectedSkill.few_shot_examples?.length || 0})</h4>
              <div className="space-y-2">
                {(selectedSkill.few_shot_examples || []).slice(0, 3).map((ex, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-2 text-xs">
                    <p className="text-gray-500"><strong>Customer:</strong> {ex.customer}</p>
                    <p className="text-gray-700 mt-1"><strong>Agent:</strong> {ex.agent}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Version History */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <History className="w-4 h-4" />
                Version History
              </h4>
              {versionsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
                </div>
              ) : versions.length === 0 ? (
                <p className="text-xs text-gray-500">No version history</p>
              ) : (
                <div className="space-y-1">
                  {versions.map((v) => {
                    const vRules = parseRulesArray(v.rules);
                    const vExamples = Array.isArray(v.few_shot_examples) ? v.few_shot_examples : [];
                    const isExpanded = expandedVersionId === v.id;
                    return (
                      <div key={v.id} className={cn(
                        'rounded-lg text-xs',
                        compareVersions[0]?.id === v.id || compareVersions[1]?.id === v.id ? 'bg-violet-50 border-2 border-[#1a1a2e]' : 'bg-gray-50'
                      )}>
                        <div
                          className="flex items-center justify-between p-2 cursor-pointer"
                          onClick={() => setExpandedVersionId(isExpanded ? null : v.id)}
                        >
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900">v{v.version}</span>
                            <span className="text-gray-500 ml-2">{new Date(v.created_at).toLocaleDateString()}</span>
                            <span className="text-gray-400 ml-2">{vRules.length} rules, {vExamples.length} examples</span>
                            {v.change_summary && <p className="text-gray-500 truncate mt-0.5">{v.change_summary}</p>}
                          </div>
                          <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => {
                                if (!compareVersions[0]) setCompareVersions([v, null]);
                                else if (!compareVersions[1]) setCompareVersions([compareVersions[0], v]);
                                else setCompareVersions([v, null]);
                              }}
                              className="px-2 py-1 text-gray-500 hover:text-violet-600 hover:bg-violet-50 rounded transition-colors"
                              title="Compare"
                            >
                              <ArrowLeftRight className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleSetVersion(v.version)}
                              disabled={settingVersion}
                              className="px-2 py-1 text-violet-600 hover:bg-violet-50 rounded font-medium transition-colors disabled:opacity-50"
                            >
                              Activate
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-2 pb-2 space-y-2 border-t border-[#1a1a2e] pt-2">
                            {vRules.length > 0 && (
                              <div>
                                <p className="font-semibold text-gray-700 mb-1">Rules:</p>
                                {vRules.map((r: string, i: number) => (
                                  <p key={i} className="text-gray-600 bg-white rounded p-1.5 mb-0.5">- {r}</p>
                                ))}
                              </div>
                            )}
                            {vExamples.length > 0 && (
                              <div>
                                <p className="font-semibold text-gray-700 mb-1">Examples:</p>
                                {vExamples.map((ex: any, i: number) => (
                                  <div key={i} className="bg-white rounded p-1.5 mb-0.5">
                                    <p className="text-gray-500"><strong>Customer:</strong> {ex.customer}</p>
                                    <p className="text-gray-700"><strong>Agent:</strong> {ex.agent}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Side-by-side comparison */}
              {compareVersions[0] && compareVersions[1] && (
                <div className="mt-4 border-2 border-[#1a1a2e] rounded-xl p-3 bg-violet-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-xs font-semibold text-gray-900">Comparing v{compareVersions[0].version} vs v{compareVersions[1].version}</h5>
                    <button onClick={() => setCompareVersions([null, null])} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {compareVersions.map((v, idx) => {
                      const rules = parseRulesArray(v!.rules);
                      return (
                        <div key={idx} className="bg-white rounded-lg p-2 text-xs">
                          <p className="font-semibold text-gray-900 mb-1">v{v!.version} ({rules.length} rules)</p>
                          {rules.slice(0, 3).map((r: string, i: number) => (
                            <p key={i} className="text-gray-600 truncate">- {r}</p>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Skill Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Create Skill</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
                <input type="text" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Greeting Style" className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl text-sm focus:ring-2 focus:ring-[#1a1a2e]/20 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} rows={4} className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl text-sm focus:ring-2 focus:ring-[#1a1a2e]/20 outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                <select value={createForm.skill_type} onChange={(e) => setCreateForm({ ...createForm, skill_type: e.target.value as 'common' | 'topic' })} className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl text-sm bg-white">
                  <option value="common">Common</option>
                  <option value="topic">Topic</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Rules</label>
                {createForm.rules.map((rule, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" value={rule} onChange={(e) => { const r = [...createForm.rules]; r[i] = e.target.value; setCreateForm({ ...createForm, rules: r }); }} placeholder="Enter a rule..." className="flex-1 px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm" />
                    {createForm.rules.length > 1 && (
                      <button type="button" onClick={() => setCreateForm({ ...createForm, rules: createForm.rules.filter((_, j) => j !== i) })} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setCreateForm({ ...createForm, rules: [...createForm.rules, ''] })} className="text-sm text-violet-600 hover:text-violet-700 font-medium">+ Add Rule</button>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 text-sm">Cancel</button>
                <button type="submit" disabled={isCreating} className="px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 text-sm disabled:opacity-50">
                  {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extraction Results Modal (Two-Panel) */}
      {showExtractionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowExtractionModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-6xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Extraction Results</h2>
                  {extractionJob && (
                    <p className="text-sm text-gray-500">{extractionJob.candidate_count} candidates found &middot; ${(extractionJob.total_cost_usd || 0).toFixed(4)} cost</p>
                  )}
                </div>
              </div>
              <button onClick={() => setShowExtractionModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {extractionLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Candidates */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Extracted Candidates</h3>
                  <div className="border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#1a1a2e] bg-gray-50">
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Name</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Type</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Status</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {candidates.map((c) => {
                          const candidateRules = parseRulesArray(c.rules);
                          const candidateExamples = Array.isArray(c.few_shot_examples) ? c.few_shot_examples : [];
                          return (
                            <tr key={c.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-900">{c.name}</p>
                                <p className="text-xs text-gray-500">{candidateRules.length} rules, {candidateExamples.length} examples</p>
                                {c.suggested_match_name && c.suggested_match_skill_id && c.status === 'pending' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMergeCandidate(c.id, c.suggested_match_skill_id!);
                                    }}
                                    disabled={processingCandidateId === c.id}
                                    className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xs font-medium text-violet-700 bg-violet-50 border-2 border-[#1a1a2e] rounded-md hover:bg-violet-100 transition-colors disabled:opacity-50"
                                  >
                                    <Merge className="w-3 h-3" />
                                    Merge into {c.suggested_match_name}
                                  </button>
                                )}
                                {c.suggested_match_name && !c.suggested_match_skill_id && (
                                  <p className="text-xs text-violet-600 mt-0.5">Suggested: {c.suggested_match_name}</p>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant={c.skill_type === 'common' ? 'info' : 'default'}>{c.skill_type}</Badge>
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant={getCandidateStatusBadge(c.status)}>{c.status}</Badge>
                              </td>
                              <td className="px-4 py-3">
                                {c.status === 'pending' && (
                                  <div className="flex items-center gap-1">
                                    {showMergeSelect === c.id ? (
                                      <div className="flex items-center gap-1">
                                        <select
                                          value={mergeTargetSkillId}
                                          onChange={(e) => setMergeTargetSkillId(e.target.value)}
                                          className="px-2 py-1 border rounded text-xs"
                                        >
                                          <option value="">Select skill...</option>
                                          {skills.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                        <button
                                          onClick={() => mergeTargetSkillId && handleMergeCandidate(c.id, mergeTargetSkillId)}
                                          disabled={!mergeTargetSkillId || processingCandidateId === c.id}
                                          className="px-2 py-1 bg-violet-500 text-white rounded text-xs disabled:opacity-50"
                                        >
                                          {processingCandidateId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Go'}
                                        </button>
                                        <button onClick={() => setShowMergeSelect(null)} className="px-1 py-1 text-gray-500 text-xs">
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => { setShowMergeSelect(c.id); setMergeTargetSkillId(c.suggested_match_skill_id || ''); }}
                                          className="p-1 text-violet-600 hover:bg-violet-50 rounded"
                                          title="Merge into existing skill - combines this candidate's rules and examples with an existing skill"
                                        >
                                          <Merge className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          onClick={() => handleCreateFromCandidate(c.id)}
                                          disabled={processingCandidateId === c.id}
                                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                                          title="Create as new skill - adds this candidate as a brand new skill"
                                        >
                                          {processingCandidateId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlusCircle className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                          onClick={() => handleDismissCandidate(c.id)}
                                          disabled={processingCandidateId === c.id}
                                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                                          title="Dismiss - discard this candidate without creating or merging"
                                        >
                                          <XCircle className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right: Existing Skills */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Existing Skills</h3>
                  <div className="border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#1a1a2e] bg-gray-50">
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Name</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Type</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Ver</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500">Rules</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {skills.map((s) => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.name}</td>
                            <td className="px-4 py-3"><Badge variant={s.skill_type === 'common' ? 'info' : 'default'}>{s.skill_type}</Badge></td>
                            <td className="px-4 py-3 text-sm text-gray-600">v{s.version}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{s.rules?.length || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsPage;
